import React, { useRef, useState, useEffect } from "react";
import { Table, Button, Modal, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import type { TDocumentDefinitions } from "pdfmake/interfaces";
import type { Content } from "pdfmake/interfaces";
import UserService from "../../services/UserService";
import { formatPhoneNumberDisplay, openNotification } from "../../utils";
import AgreementService from "../../services/AgreementService";
import { loadPdfFontFallback } from "../../utils/pdfFontFallback";

let pdfMakeInstance: any = null;

const loadPdfMake = async (): Promise<any> => {
  if (pdfMakeInstance) return pdfMakeInstance;
  const [pdfMakeModule, pdfFontsModule] = await Promise.all([
    import("pdfmake/build/pdfmake"),
    import("pdfmake/build/vfs_fonts"),
  ]);
  const pdfMake = (pdfMakeModule as any).default || pdfMakeModule;
  const pdfFonts = (pdfFontsModule as any).default || pdfFontsModule;

  const vfs =
    (pdfFonts as any)?.pdfMake?.vfs ||
    (pdfFonts as any)?.vfs ||
    (pdfMake as any)?.vfs ||
    (pdfMakeModule as any)?.vfs ||
    (pdfFontsModule as any)?.pdfMake?.vfs ||
    (pdfFontsModule as any)?.vfs ||
    (typeof pdfFonts === "object" ? pdfFonts : null);

  (pdfMake as any).vfs = vfs && typeof vfs === "object" ? vfs : {};
  pdfMakeInstance = pdfMake;
  return pdfMakeInstance;
};

const normalizeText = (value: string) => {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length ? normalized : null;
};

type InlineTextStyles = {
  bold?: boolean;
  italics?: boolean;
  decoration?: "underline" | "lineThrough" | "overline";
  fontSize?: number;
  lineHeight?: number;
};

const applyStylesToTextParts = (parts: Array<Content | string>, styles: InlineTextStyles) => {
  if (!styles || !Object.keys(styles).length) return parts;
  const styleKeys: Array<keyof InlineTextStyles> = ["bold", "italics", "decoration", "fontSize", "lineHeight"];

  return parts.map((part) => {
    if (typeof part === "string") {
      return { text: part, ...styles };
    }

    if (part && typeof part === "object") {
      const updatedPart: any = { ...part };
      styleKeys.forEach((key) => {
        const value = styles[key];
        if (value !== undefined && updatedPart[key] === undefined) {
          updatedPart[key] = value;
        }
      });
      if (updatedPart.text === undefined) updatedPart.text = "";
      return updatedPart;
    }

    return part;
  });
};

const getInlineTextStyles = (element: HTMLElement): InlineTextStyles => {
  const styleAttr = element.getAttribute("style");
  if (!styleAttr) return {};

  return styleAttr.split(";").reduce<InlineTextStyles>((styles, rule) => {
    const [prop, rawValue] = rule.split(":").map((item) => item?.trim().toLowerCase());
    if (!prop || !rawValue) return styles;

    if (prop === "font-weight" && (rawValue === "bold" || Number(rawValue) >= 600)) {
      styles.bold = true;
    }
    if (prop === "font-style" && rawValue === "italic") {
      styles.italics = true;
    }
    if (prop === "text-decoration" && rawValue.includes("underline")) {
      styles.decoration = "underline";
    }
    if (prop === "font-size") {
      const parsed = parseFloat(rawValue.replace("px", "").trim());
      if (!Number.isNaN(parsed)) styles.fontSize = parsed;
    }
    if (prop === "line-height") {
      if (rawValue === "normal") {
        styles.lineHeight = 1.2;
      } else {
        const parsed = parseFloat(rawValue);
        if (!Number.isNaN(parsed)) styles.lineHeight = parsed;
      }
    }
    return styles;
  }, {});
};

const flattenContent = (
  items: Array<Content | string | Array<Content | string> | null>
): Array<Content | string> =>
  items.reduce<Array<Content | string>>((acc, item) => {
    if (Array.isArray(item)) {
      acc.push(...flattenContent(item));
    } else if (item !== null) {
      acc.push(item);
    }
    return acc;
  }, []);

function htmlNodeToPdfContent(node: ChildNode): Content | string | Array<Content | string> | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return normalizeText(node.textContent || "");
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as HTMLElement;
  const children = buildChildContent(Array.from(element.childNodes));
  const tag = element.tagName.toLowerCase();
  const inlineStyles = getInlineTextStyles(element);

  if (tag === "br") return "\n";

  const textContent = children.length ? children : [""];
  const styledTextContent = applyStylesToTextParts(textContent, inlineStyles);

  switch (tag) {
    case "strong":
    case "b":
      return { text: applyStylesToTextParts(styledTextContent, { bold: true }), ...inlineStyles, bold: true };
    case "em":
    case "i":
      return { text: applyStylesToTextParts(styledTextContent, { italics: true }), ...inlineStyles, italics: true };
    case "u":
      return {
        text: applyStylesToTextParts(styledTextContent, { decoration: "underline" }),
        ...inlineStyles,
        decoration: "underline",
      };
    case "a":
      return {
        text: styledTextContent,
        link: element.getAttribute("href") || undefined,
        color: "#0b57d0",
        ...inlineStyles,
      };
    case "h1":
      return { text: applyStylesToTextParts(styledTextContent, { fontSize: 18, bold: true }), style: "h1", ...inlineStyles };
    case "h2":
      return { text: applyStylesToTextParts(styledTextContent, { fontSize: 15, bold: true }), style: "h2", ...inlineStyles };
    case "h3":
      return { text: applyStylesToTextParts(styledTextContent, { fontSize: 13, bold: true }), style: "h3", ...inlineStyles };
    case "ul": {
      const items = flattenContent(Array.from(element.children).map((child) => htmlNodeToPdfContent(child))).filter(
        (child): child is Content | string => child !== null && child !== ""
      );
      return items.length ? { ul: items } : null;
    }
    case "ol": {
      const items = flattenContent(Array.from(element.children).map((child) => htmlNodeToPdfContent(child))).filter(
        (child): child is Content | string => child !== null && child !== ""
      );
      return items.length ? { ol: items } : null;
    }
    case "li":
      return { text: styledTextContent, margin: [0, 0, 0, 4], ...inlineStyles };
    case "p":
      return { text: styledTextContent, margin: [0, 0, 0, 8], ...inlineStyles };
    case "div":
      return { text: styledTextContent, margin: [0, 0, 0, 8], ...inlineStyles };
    default:
      return { text: styledTextContent, ...inlineStyles };
  }
}

function buildChildContent(nodes: ChildNode[]): Array<Content | string> {
  return flattenContent(nodes.map((node) => htmlNodeToPdfContent(node))).filter(
    (item): item is Content | string => item !== null && item !== ""
  );
}

const htmlToPdfContent = (html: string): Content[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const bodyContent = buildChildContent(Array.from(doc.body.childNodes)).filter(
    (item): item is Content | string => item !== null && item !== ""
  );

  if (!bodyContent.length) {
    const fallback = normalizeText(doc.body.textContent || "");
    return fallback ? [{ text: fallback, margin: [0, 0, 0, 8] }] : [];
  }

  return bodyContent as Content[];
};

interface Contract {
  id: string;
  key: string;
  name: string;
  publicationDate: string;
  lastActionDate: string;
  content: string;
}

/**
 * Profil verisine identityNumber eklendi.
 */
interface ProfileData {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  address?: string;
  identityNumber?: string; // Eklenen alan
  isPhoneVerified?: boolean;
  roles?: Array<{ name: string }>;
}

const Contracts: React.FC<{
  profileCompletion: number;
  partner: boolean;
  profileData: ProfileData;
}> = ({ profileCompletion, partner, profileData }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [agreementData, setAgreementData] = useState<Contract[]>([]);
  const [message, setMessage] = useState<string[]>([]);

  // Modal içerikteki sözleşme HTML'ini göstermek için kullanılacak <div> ref'i
  const contractRef = useRef<HTMLDivElement>(null);

  /**
   * Tüm temel bilgiler (adres, telefon, e-posta, TC Kimlik No) var mı?
   * Kullanıcı bu dört bilgiyi doldurmadıysa, sözleşme modalı açılmayacak
   * ve "Sözleşmeyi Onayla" butonu devre dışı kalacak.
   */
  const hasEssentialPartnerData = !!(
    profileData?.email &&
    profileData?.phoneNumber &&
    profileData?.address &&
    profileData?.identityNumber &&
    profileData?.isPhoneVerified === true
  );

  useEffect(() => {
    const missingFields: string[] = [];
    if (!profileData?.email) missingFields.push("E-Posta");
    if (!profileData?.phoneNumber) missingFields.push("Telefon Numarası");
    if (!profileData?.address) missingFields.push("Adres");
    if (!profileData?.identityNumber) missingFields.push("Kimlik Numarası");
    if (profileData?.isPhoneVerified !== true) missingFields.push("Telefon Doğrulaması");

    setMessage(missingFields);
  }, [profileData]);


  // AgreementService'den sözleşmeleri çekiyoruz, id=5 olanı ayıklıyoruz
  const fetchAgreement = async () => {
    try {
      const res = await AgreementService.getAllAgreements();
      // id=5 olan sözleşmeyi bul
      const found = res.data.find((a: any) => a.id === 5);
      if (found) {
        // Gelen sözleşme verisini Contract arayüzüne uyarlıyoruz
        const newContract: Contract = {
          id: found.id.toString(),
          key: found.id.toString(),
          name: found.name || "Sözleşme Adı",
          publicationDate: found.publicationDate || "",
          lastActionDate: found.lastActionDate || "",
          content: found.contractHtml || "<p>Sözleşme içeriği bulunamadı.</p>",
        };
        setAgreementData([newContract]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAgreement();
  }, []);

  // Modal açma
  const showModal = (id: string) => {
    if (!hasEssentialPartnerData) {
      openNotification("warning", "Eksik Bilgi", "Önce tüm bilgilerinizi doldurun.");
      return;
    }
    const contract = agreementData.find((c) => c.id === id);
    if (contract) {
      // Varsa placeholder'ları doldur, yoksa olduğu gibi bırak
      let replacedContent = contract.content;

      const today = new Date();
      const formattedDate = today.toLocaleDateString('tr-TR').split('.').join('/');

      const placeholders: { [key: string]: string } = {
        'TEST': `${profileData.id}`,
        '01/06/2024': formattedDate,
        '[İş Ortağı İsim]': `${profileData.firstName || "Ad"} ${profileData.lastName || "Soyad"}`,
        "[İş Ortağı Adresi]": profileData.address || "",
        "[İş Ortağı Telefonu]": formatPhoneNumberDisplay(profileData.phoneNumber) || "",
        "[İş Ortağı E-posta Adresi]": profileData.email || "",
        "[İş Ortağı TC Kimlik No]": profileData.identityNumber || "",
        'İş Ortağı ': `${profileData.firstName || "Ad"} ${profileData.lastName || "Soyad"} `,
        '"İş Ortağı"': 'İŞ ORTAĞI',
        'Kontrol X Şirketi ("KontrolX")': "NORMCERT UYGUNLUK DEĞERLENDİRME HİZMETLERİ LTD. ŞTİ. (KONTROLX)",
        "[Kontrol X Adresi]": "İkitelli Organize Sanayi Bölgesi Giyim Sanatkarları Sanayi Sitesi 2.Ada A Blok No:103 Başakşehir - İstanbul",
        "[Kontrol X Telefonu]": "0212 407 04 05",
        "[Kontrol X E-posta Adresi]": "info@kontrolx.com.tr",

      };

      Object.entries(placeholders).forEach(([searchValue, replaceValue]) => {
        if (replacedContent.includes(searchValue)) {
          replacedContent = replacedContent.replaceAll(searchValue, replaceValue);
        }
      });

      const newContract = { ...contract, content: replacedContent };
      setSelectedContract(newContract);
      setIsModalVisible(true);
    }
  };

  // Modal kapama
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // "Sözleşmeyi Onayla" butonu
  const handleUpdateIsPartner = (value: boolean) => {
    if (!hasEssentialPartnerData) {
      openNotification("warning", "Eksik Bilgi", "Önce tüm bilgilerinizi doldurun.");
      return;
    }
    UserService.updateMe({ isPartner: value })
      .then(() => {
        openNotification(
          "success",
          "Başarılı",
          "İş ortağı işleminiz güncellendi. Artık teklif oluşturabilirsiniz."
        );
        localStorage.setItem("isPartner", "true");
        window.location.reload();
      })
      .catch((e: Error) => {
        console.error(e);
        openNotification(
          "error",
          "Hata",
          "İş ortağı işleminiz güncellenirken bir hata oluştu."
        );
      });
  };

  // "Sözleşmeyi İndir" butonu
  const handleDownload = async (id: string) => {
    if (!hasEssentialPartnerData) {
      openNotification("warning", "Eksik Bilgi", "Önce tüm bilgilerinizi doldurun.");
      return;
    }
    const contract = agreementData.find((c) => c.id === id);
    if (!contract) return;

    let replacedContent = contract.content;

    const today = new Date();
    const formattedDate = today.toLocaleDateString('tr-TR').split('.').join('/');

    const placeholders: { [key: string]: string } = {
        'TEST': `${profileData.id}`,
        '01/06/2024': formattedDate,
        '[İş Ortağı İsim]': `${profileData.firstName || "Ad"} ${profileData.lastName || "Soyad"}`,
        "[İş Ortağı Adresi]": profileData.address || "",
        "[İş Ortağı Telefonu]": formatPhoneNumberDisplay(profileData.phoneNumber) || "",
        "[İş Ortağı E-posta Adresi]": profileData.email || "",
        "[İş Ortağı TC Kimlik No]": profileData.identityNumber || "",
        'İş Ortağı ': `${profileData.firstName || "Ad"} ${profileData.lastName || "Soyad"} `,
        '"İş Ortağı"': 'İŞ ORTAĞI',
        'Kontrol X Şirketi ("KontrolX")': "NORMCERT UYGUNLUK DEĞERLENDİRME HİZMETLERİ LTD. ŞTİ. (KONTROLX)",
        "[Kontrol X Adresi]": "İkitelli Organize Sanayi Bölgesi Giyim Sanatkarları Sanayi Sitesi 2.Ada A Blok No:103 Başakşehir - İstanbul",
        "[Kontrol X Telefonu]": "0212 407 04 05",
        "[Kontrol X E-posta Adresi]": "info@kontrolx.com.tr",
    };

    Object.entries(placeholders).forEach(([searchValue, replaceValue]) => {
      if (replacedContent.includes(searchValue)) {
        replacedContent = replacedContent.replaceAll(searchValue, replaceValue);
      }
    });

    try {
      const content = htmlToPdfContent(replacedContent);

      if (!content.length) {
        openNotification("error", "Hata", "Sözleşme içeriği oluşturulamadı.");
        return;
      }

      const pdfMake = await loadPdfMake();
      const pdfFontFallback = await loadPdfFontFallback();
      pdfFontFallback.registerPdfMakeFonts(pdfMake);
      const docDefinition: TDocumentDefinitions = {
        pageSize: "A4",
        pageMargins: [40, 50, 40, 50],
        content: pdfFontFallback.transformPdfMakeContent(content) as Content[],
        defaultStyle: { font: "Urbanist", fontSize: 11, lineHeight: 1.4 },
        styles: {
          h1: { font: "Urbanist", fontSize: 18, bold: true, margin: [0, 0, 0, 10] },
          h2: { font: "Urbanist", fontSize: 15, bold: true, margin: [0, 0, 0, 8] },
          h3: { font: "Urbanist", fontSize: 13, bold: true, margin: [0, 0, 0, 6] },
        },
      };

      pdfMake.createPdf(docDefinition).download(`${contract.name}.pdf`);
    } catch (error) {
      console.error("Contract download failed:", error);
      openNotification("error", "Hata", "Sözleşme indirilemedi.");
    }
  };

  // Tablonun kolonları
  const columns: ColumnsType<Contract> = [
    {
      title: "Sözleşme Adı",
      dataIndex: "name",
      key: "name",
      render: (text, record) => {
        return (
          <Tooltip
            title={
              !hasEssentialPartnerData
                ? `Sözleşmeyi onaylamanız için Kullanıcı Bilgilerinizi (${message}) eksiksiz tamamlamanız gerekmektedir.`
                : ""
            }
          >
            <span
              onClick={() => {
                if (hasEssentialPartnerData) {
                  showModal(record.id);
                }
              }}
              style={{
                color: hasEssentialPartnerData ? "#1890ff" : "gray",
                cursor: hasEssentialPartnerData ? "pointer" : "not-allowed",
                textDecoration: hasEssentialPartnerData ? "underline" : "none",
              }}
            >
              {text}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "İşlem",
      key: "action",
      render: (_, record) => {
        const canApprove =
          profileCompletion === 100 && profileData?.isPhoneVerified && !partner && hasEssentialPartnerData;

        return (
          <div>
            <Tooltip
              title={
                !hasEssentialPartnerData
                  ? `Sözleşmeyi onaylamanız için Kullanıcı Bilgilerinizi (${message}) eksiksiz tamamlamanız gerekmektedir.`
                  : profileCompletion !== 100 || !profileData?.isPhoneVerified
                    ? `Sözleşmeyi onaylamanız için Kullanıcı Bilgilerinizi (${message}) eksiksiz tamamlamanız gerekmektedir.`
                    : partner
                      ? "İş ortaklarımız arasındasınız. Yeniden onaya gerek bulunmamaktadır."
                      : ""
              }
            >
              <Button
                type="primary"
                style={{ marginRight: 10 }}
                className="d-button dark-button width-auto"
                disabled={!canApprove || partner}
                onClick={() => handleUpdateIsPartner(true)}
              >
                Sözleşmeyi Onayla
              </Button>
            </Tooltip>
            {/* Sözleşmeyi indir butonu her zaman aktif => 
                ama veriler eksikse fonksiyon içinde uyarı */}
            <Button
              type="primary"
              onClick={() => handleDownload(record.id)}
              className="d-button dark-button width-auto"
            >
              Sözleşmeyi İndir
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <Table
        scroll={{ x: 768 }}
        columns={columns}
        dataSource={agreementData}
        pagination={false}
      />

      <Modal
        centered
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
        title={selectedContract?.name}
        width={"auto"}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
      >
        <div
          ref={contractRef}
          style={{
            border: 1,
            borderColor: "black",
            width: "210mm",
            minHeight: "auto",
            background: "#fff",
            margin: "0 auto",
            padding: 20,
            boxSizing: "border-box",
            transformOrigin: "top left",
            position: "relative",
          }}
        >
          <style>
            {`
              @media (max-width: 600px) {
                div[style*="210mm"] {
                  transform: scale(0.6);
                }
              }
              p {
                margin-bottom: 10px;
              }
            `}
          </style>

          {selectedContract && (
            <div dangerouslySetInnerHTML={{ __html: selectedContract.content }} />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Contracts;
